/**
 * Composes the reload-loop guard (versionReloadGuard + decideReload) with its
 * side effects: an in-memory latch so one guard instance never reloads twice,
 * a Sentry breadcrumb on give-up, and window.location.reload(). Degrades to no
 * auto-reload when sessionStorage is unavailable (e.g. Safari private mode).
 *
 * Returns the number of ms until the next attempt is allowed when the call was
 * blocked purely by the cooldown (so the caller can schedule a retry — the
 * fetched version doesn't change on a stuck deploy, so nothing else will re-run
 * the effect); returns 0 in every other case (reloaded, gave up, already
 * reloading, or storage unavailable).
 */
import * as Sentry from '@sentry/react'

import { versionReloadGuard } from '@/services/versionReloadGuard.service'

import { RELOAD_COOLDOWN_MS, decideReload } from './versionFreshness'

export function reloadWithGuard(targetVersion: string, isReloadingRef: { current: boolean }): number {
  if (isReloadingRef.current) return 0
  if (!versionReloadGuard.isAvailable()) return 0

  const now = Date.now()
  const decision = decideReload({ record: versionReloadGuard.get(), targetVersion, now })

  if (decision.gaveUp) {
    Sentry.addBreadcrumb({
      category: 'app-freshness',
      level: 'warning',
      message: 'version-reload budget exhausted',
      data: { target: targetVersion },
    })
    return 0
  }

  if (!decision.shouldReload) {
    // Cooldown-blocked: report how long until the next attempt is allowed.
    return Math.max(0, decision.nextRecord.lastAttemptAt + RELOAD_COOLDOWN_MS - now)
  }

  versionReloadGuard.set(decision.nextRecord)
  isReloadingRef.current = true
  window.location.reload()
  return 0
}
